import type { ComputedRef, InjectionKey, MaybeRefOrGetter, Ref, WritableComputedRef } from 'vue';
import type { NativeType, NotUndefined } from '#layers/base/public';
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { BaseFormControlProps } from './use-base-form-field-state';
import { camelCase, kebabCase } from 'scule';
import { computed, getCurrentInstance, inject, provide, toValue } from 'vue';
import { callOrReturn } from '#layers/base/public';
import { useBaseFormField } from './use-base-form-field-state';
import { useFieldNullable } from './use-field-nullable';

type InferDefault<T> = (() => T) | (T extends NativeType ? T : never) | undefined;

type InferModelReturn<T, TDefault extends InferDefault<T> | undefined> = WritableComputedRef<
	TDefault extends undefined
		? T
		: TDefault extends () => T
			? ReturnType<NotUndefined<TDefault>> extends undefined
				? T
				: NotUndefined<T>
			: NotUndefined<T>
>;

export interface PrimitiveFormFieldContext {
	model: WritableComputedRef<unknown>;
	field: MaybeRefOrGetter<ResolvedMonospacePrimitiveField | undefined>;
	disabled: MaybeRefOrGetter<boolean>;
}

interface UsePrimitiveFormFieldOptions<T, TDefault extends InferDefault<T>> {
	model: Ref<T>;
	defaultValue?: TDefault;
}

/**
 * @internal
 */
const primitiveFormFieldContextKey = Symbol('primitiveFormFieldContextKey') as InjectionKey<PrimitiveFormFieldContext>;

/**
 * Provides the field's context to the controls rendered for it and returns the merged context.
 *
 * The returned and provided `model` refuses writes while the merged `disabled` is true, so no
 * control, menu action, or shortcut can change a field the form does not let the user edit. Write
 * the field's value through it rather than through `context.model`.
 */
export function providePrimitiveFormFieldContext(context: PrimitiveFormFieldContext) {
	const baseFormField = useBaseFormField();

	const disabled = computed(() => {
		const baseDisabled = baseFormField.disabled.value;
		const primitiveDisabled = toValue(context.disabled);
		return baseDisabled || primitiveDisabled;
	});

	const model = computed({
		get: () => context.model.value,
		set: (value) => {
			if (disabled.value) return;
			context.model.value = value;
		},
	});

	const mergedContext = {
		...context,
		model,
		disabled,
	};

	provide(primitiveFormFieldContextKey, mergedContext);

	return mergedContext;
}

/**
 * @internal
 */
function injectPrimitiveFormFieldContext() {
	return inject(primitiveFormFieldContextKey, null);
}

type ModelValueReturn<T, TDefault extends InferDefault<T>, HasModelValue extends boolean> = HasModelValue extends true ? ReturnType<typeof usePrimitiveFormFieldModel<T, TDefault>> : never;

type UsePrimitiveFormFieldReturn<T, TDefault extends InferDefault<T>, HasModelValue extends boolean> = ReturnType<typeof useBaseFormField> & {
	modelValue: ModelValueReturn<T, TDefault, HasModelValue>;
	field: ComputedRef<ResolvedMonospacePrimitiveField | undefined>;
	nullable: ComputedRef<boolean>;
};

export function usePrimitiveFormField(props: MaybeRefOrGetter<BaseFormControlProps>): UsePrimitiveFormFieldReturn<unknown, undefined, false>;
export function usePrimitiveFormField<T, TDefault extends InferDefault<T> = undefined>(props: MaybeRefOrGetter<BaseFormControlProps>, options: UsePrimitiveFormFieldOptions<T, TDefault>): UsePrimitiveFormFieldReturn<T, TDefault, true>;

export function usePrimitiveFormField<T, TDefault extends InferDefault<T> = undefined>(
	props: MaybeRefOrGetter<BaseFormControlProps>,
	options?: UsePrimitiveFormFieldOptions<T, TDefault>,
) {
	const base = useBaseFormField(props);
	const primitiveFormFieldContext = injectPrimitiveFormFieldContext();

	const disabled = computed<boolean>(() => base.disabled.value || toValue(primitiveFormFieldContext?.disabled) || false);
	const field = computed(() => toValue(primitiveFormFieldContext?.field));
	const nullable = useFieldNullable(field);

	return {
		...base,
		disabled,
		field,
		nullable,
		modelValue: options ? usePrimitiveFormFieldModel(options.model, options.defaultValue) : undefined,
	};
}

/**
 * Use the provided model or use the model of the parent FormField block if it exists as a fallback.
 *
 * @param model The model from the component, usually declared with `defineModel`
 * @param defaultValue A function that returns the default value if both the provided model and the parent model are nullish.
 */
export function usePrimitiveFormFieldModel<T, TDefault extends InferDefault<T>>(model: Ref<T>, defaultValue?: TDefault, name: string = 'modelValue'): InferModelReturn<T, TDefault> {
	const formFieldContext = injectPrimitiveFormFieldContext();
	const vm = getCurrentInstance();

	if (!vm) {
		throw new Error('usePrimitiveFormFieldModel must be called within a component setup');
	}

	const camelizedName = camelCase(name);
	const hyphenatedName = kebabCase(name);
	return computed({
		get: () => {
			if (model.value !== undefined) return model.value;
			if (formFieldContext?.model.value !== undefined) return formFieldContext.model.value as T;
			return callOrReturn(defaultValue) as T;
		},
		set: (value: T) => {
			const rawProps = vm.vnode!.props;

			if (rawProps
				// check if parent has passed v-model
				&& (name in rawProps
					|| camelizedName in rawProps
					|| hyphenatedName in rawProps)
				&& (`onUpdate:${name}` in rawProps
					|| `onUpdate:${camelizedName}` in rawProps
					|| `onUpdate:${hyphenatedName}` in rawProps)) {
				model.value = value;
			}

			if (formFieldContext) {
				formFieldContext.model.value = value;
			}
		},
	}) as InferModelReturn<T, TDefault>;
}
